/**
 * @module engine/input/PointerController
 * @summary Unified mouse + touch + pen input for the canvas (Pointer Events).
 * @description
 *   The original split logic between mouse handlers (full editing) and touch
 *   handlers (pinch + pan only). This unifies them on Pointer Events so a SINGLE
 *   finger can select/drag/resize/connect — fixing the "can't edit by touch" gap
 *   — while two fingers pinch-zoom/pan. `setPointerCapture` keeps a drag alive
 *   when the pointer leaves the canvas (replacing the old document-level mouse
 *   listeners). Touch gets a larger handle hit-slop.
 * @ported-from JCC PFD Engineering Tool v1.1.html lines 3445-3597 (mouse),
 *   4248-4275 (touch), 3588-3593 (wheel), 4107-4124 (contextmenu).
 */
import { S, emit, markDirty } from '../state';
import type { DocSnapshot } from '../state';
import { toW, getR, snap } from '../coords';
import { hitShape, hitHandle, hitConn, hitConnSegHandle } from '../hitTest';
import { nearPort } from '../geometry';
import { pushUndo, captureSnapshot, pushCapturedUndo } from '../history';
import { computeSmartGuides } from '../smartGuides';
import { expandGroupSelection } from '../ops/transformOps';
import type { Shape } from '../../types';

/** Callbacks the controller needs from the engine for React-facing UI. */
export interface PointerCallbacks {
  openLabelEdit: (shape: Shape, sx: number, sy: number) => void;
  openContextMenu: (info: {
    clientX: number;
    clientY: number;
    hasSelection: boolean;
    hasClipboard: boolean;
  }) => void;
  updateZoomLabel: () => void;
}

/** Attach all pointer/wheel/contextmenu listeners. Returns a detach fn. */
export function attachPointerControls(
  canvas: HTMLCanvasElement,
  cb: PointerCallbacks,
): () => void {
  /** Active pointers (for multi-touch pinch). */
  const pointers = new Map<number, { x: number; y: number }>();
  let primaryId: number | null = null;
  // Pre-mutation snapshot taken when a drag/resize STARTS; pushed onto the
  // undo stack at pointer-up only if something actually changed.
  let dragSnap: DocSnapshot | null = null;

  const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

  function onPointerDown(e: PointerEvent): void {
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    // Two-finger gesture → start pinch; abort any in-progress edit.
    if (pointers.size === 2) {
      const [a, b] = [...pointers.values()];
      S.pinchDist = Math.hypot(a.x - b.x, a.y - b.y);
      S.pinchZoom = S.zoom;
      dragSnap = null;
      S.isDragging = S.isResizing = S.isConnDragging = S.isPanning = false;
      S.lassoStart = null;
      S.lassoRect = null;
      return;
    }
    if (pointers.size > 2) return;

    canvas.setPointerCapture(e.pointerId);
    primaryId = e.pointerId;
    const r = getR();
    const sx = e.clientX - r.left;
    const sy = e.clientY - r.top;
    const wc = toW(sx, sy);
    S.mouseDown = true;
    S.mouseMoved = false;
    const isTouch = e.pointerType === 'touch';
    const slop = isTouch ? 18 : 7; // bigger handle target on touch

    // Middle-mouse or Space → pan.
    if (e.button === 1 || (S.spaceDown && e.button === 0)) {
      S.isPanning = true;
      S.panStart = { sx: e.clientX, sy: e.clientY, px: S.panX, py: S.panY };
      if (e.button === 1) e.preventDefault();
      return;
    }
    if (S.tool === 'pan' && e.button === 0) {
      S.isPanning = true;
      S.panStart = { sx: e.clientX, sy: e.clientY, px: S.panX, py: S.panY };
      return;
    }

    if (S.tool === 'select') {
      // 1) Connection segment handles (highest priority).
      const csh = hitConnSegHandle(wc.x, wc.y, slop + 2);
      if (csh) {
        pushUndo();
        S.isConnDragging = true;
        S.connDragState = {
          cn: csh.cn,
          segIdx: csh.segIdx,
          pts: csh.pts,
          startX: wc.x,
          startY: wc.y,
          origMidX: csh.cn.midX,
          origMidY: csh.cn.midY,
        };
        markDirty();
        return;
      }
      // 2) Resize handle on a selected shape.
      for (const id of S.selected) {
        const s = S.shapes.find((x) => x.id === id);
        if (!s || s.locked) continue;
        const h = hitHandle(s, wc.x, wc.y, slop);
        if (h) {
          dragSnap = captureSnapshot();
          S.isResizing = true;
          S.resizeState = { shape: s, handle: h, sx: wc.x, sy: wc.y, orig: { ...s } };
          return;
        }
      }
      // 3) Hit a shape → select (+group) and start dragging.
      const hit = hitShape(wc.x, wc.y);
      if (hit) {
        const groupMembers = expandGroupSelection(hit.id);
        if (e.shiftKey) {
          if (S.selected.includes(hit.id))
            S.selected = S.selected.filter((x) => !groupMembers.includes(x));
          else groupMembers.forEach((g) => !S.selected.includes(g) && S.selected.push(g));
        } else {
          S.selected = groupMembers.slice();
        }
        // dragOffsets MUST be derived fresh from the current selection.
        S.dragOffsets = [];
        for (const id of S.selected) {
          const s = S.shapes.find((x) => x.id === id);
          if (s) S.dragOffsets.push({ id, dx: wc.x - s.x, dy: wc.y - s.y });
        }
        dragSnap = captureSnapshot();
        S.isDragging = true;
        markDirty();
        emit('selection');
        return;
      }
      // 4) Hit a connection.
      const ch = hitConn(wc.x, wc.y, slop);
      if (ch) {
        S.selected = [ch.id];
        markDirty();
        emit('selection');
        return;
      }
      // 5) Empty canvas: touch → pan, mouse → lasso.
      if (isTouch) {
        S.isPanning = true;
        S.panStart = { sx: e.clientX, sy: e.clientY, px: S.panX, py: S.panY };
      } else {
        if (!e.shiftKey) {
          S.selected = [];
          emit('selection');
        }
        S.lassoStart = { x: wc.x, y: wc.y };
      }
      markDirty();
    }

    if (S.tool === 'connect') {
      const hit = hitShape(wc.x, wc.y);
      if (hit) {
        const port = nearPort(hit, wc.x, wc.y);
        if (!S.isConnecting) {
          S.connectFrom = { shape: hit, port };
          S.isConnecting = true;
        } else {
          if (hit.id !== S.connectFrom!.shape.id) {
            pushUndo();
            S.connections.push({
              id: 'c' + ++S.shapeCounter,
              fromId: S.connectFrom!.shape.id,
              fromPort: S.connectFrom!.port,
              toId: hit.id,
              toPort: nearPort(hit, wc.x, wc.y),
              label: '',
              lineStyle: 'solid',
              lineWidth: 1.5,
              color: '#1a1a1a',
              arrowHead: 'forward',
            });
            emit('doc');
          }
          S.isConnecting = false;
          S.connectFrom = null;
          S.connectPreview = null;
        }
      } else if (S.isConnecting) {
        S.isConnecting = false;
        S.connectFrom = null;
        S.connectPreview = null;
      }
      markDirty();
    }
  }

  function onPointerMove(e: PointerEvent): void {
    if (pointers.has(e.pointerId)) pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    // Pinch-zoom + two-finger pan.
    if (pointers.size === 2 && S.pinchDist) {
      const [a, b] = [...pointers.values()];
      const d = Math.hypot(a.x - b.x, a.y - b.y);
      const r = getR();
      const cx = (a.x + b.x) / 2 - r.left;
      const cy = (a.y + b.y) / 2 - r.top;
      const nz = clamp(S.pinchZoom * (d / S.pinchDist), 0.2, 4);
      S.panX = cx - (cx - S.panX) * (nz / S.zoom);
      S.panY = cy - (cy - S.panY) * (nz / S.zoom);
      S.zoom = nz;
      cb.updateZoomLabel();
      markDirty();
      return;
    }

    const r = getR();
    const sx = e.clientX - r.left;
    const sy = e.clientY - r.top;
    const wc = toW(sx, sy);
    S.coordsX = wc.x;
    S.coordsY = wc.y;
    emit('status'); // coords readout

    const hit = hitShape(wc.x, wc.y);
    const hid = hit ? hit.id : null;
    if (hid !== S.hoveredId) {
      S.hoveredId = hid;
      markDirty();
    }
    if (S.isConnecting) {
      S.connectPreview = { x: wc.x, y: wc.y };
      markDirty();
    }
    if (S.isPanning && S.panStart) {
      S.panX = S.panStart.px + (e.clientX - S.panStart.sx);
      S.panY = S.panStart.py + (e.clientY - S.panStart.sy);
      markDirty();
      return;
    }
    // Connection segment drag.
    if (S.isConnDragging && S.connDragState && S.mouseDown) {
      S.mouseMoved = true;
      const { cn, segIdx, pts } = S.connDragState;
      const dx = wc.x - S.connDragState.startX;
      const dy = wc.y - S.connDragState.startY;
      const p0 = pts[segIdx];
      const p1 = pts[segIdx + 1];
      const isHoriz = Math.abs(p0.y - p1.y) < Math.abs(p0.x - p1.x);
      if (isHoriz) {
        cn.midX = S.connDragState.origMidX !== undefined ? S.connDragState.origMidX : (p0.x + p1.x) / 2;
        cn.midY = (S.connDragState.origMidY || p0.y) + dy;
      } else {
        const baseMidX =
          S.connDragState.origMidX !== undefined
            ? S.connDragState.origMidX
            : (pts[0].x + pts[pts.length - 1].x) / 2;
        cn.midX = snap(baseMidX + dx);
        if (S.connDragState.origMidY !== undefined) cn.midY = S.connDragState.origMidY;
      }
      markDirty();
      return;
    }
    // Resize.
    if (S.isResizing && S.resizeState) {
      const { shape: s, handle: h, sx: osx, sy: osy, orig: o } = S.resizeState;
      const dx = wc.x - osx;
      const dy = wc.y - osy;
      const id = h.id;
      if (id === 'e' || id === 'ne' || id === 'se') s.w = Math.max(20, o.w + dx);
      if (id === 's' || id === 'se' || id === 'sw') s.h = Math.max(20, o.h + dy);
      if (id === 'w' || id === 'nw' || id === 'sw') {
        s.x = o.x + dx;
        s.w = Math.max(20, o.w - dx);
      }
      if (id === 'n' || id === 'nw' || id === 'ne') {
        s.y = o.y + dy;
        s.h = Math.max(20, o.h - dy);
      }
      markDirty();
      return;
    }
    // Drag selected shapes.
    if (S.isDragging && S.mouseDown) {
      S.mouseMoved = true;
      for (const o of S.dragOffsets) {
        const s = S.shapes.find((x) => x.id === o.id);
        if (!s || s.locked) continue;
        s.x = snap(wc.x - o.dx);
        s.y = snap(wc.y - o.dy);
      }
      computeSmartGuides(S.selected);
      markDirty();
      return;
    }
    // Lasso.
    if (S.lassoStart && S.mouseDown) {
      const lx = Math.min(S.lassoStart.x, wc.x);
      const ly = Math.min(S.lassoStart.y, wc.y);
      S.lassoRect = {
        x: lx,
        y: ly,
        w: Math.abs(wc.x - S.lassoStart.x),
        h: Math.abs(wc.y - S.lassoStart.y),
      };
      markDirty();
    }
    // Cursor feedback (mouse only).
    if (e.pointerType === 'mouse') updateCursor(wc.x, wc.y);
  }

  function updateCursor(wx: number, wy: number): void {
    if (S.tool === 'select' && S.selected.length === 1) {
      const s = S.shapes.find((x) => x.id === S.selected[0]);
      if (s) {
        const h = hitHandle(s, wx, wy);
        if (h) {
          const id = h.id;
          canvas.style.cursor =
            id === 'nw' || id === 'se'
              ? id === 'nw'
                ? 'nw-resize'
                : 'se-resize'
              : id === 'ne' || id === 'sw'
                ? id === 'ne'
                  ? 'ne-resize'
                  : 'sw-resize'
                : id === 'n' || id === 's'
                  ? 'ns-resize'
                  : 'ew-resize';
        } else {
          canvas.style.cursor = hitShape(wx, wy) ? 'move' : 'default';
        }
        return;
      }
    }
    if (S.tool === 'select' && hitConnSegHandle(wx, wy)) {
      canvas.style.cursor = 'ew-resize';
      return;
    }
    canvas.style.cursor =
      S.tool === 'pan' || S.spaceDown ? 'grab' : S.tool === 'connect' ? 'crosshair' : 'default';
  }

  function onPointerUp(e: PointerEvent): void {
    pointers.delete(e.pointerId);
    if (pointers.size < 2) S.pinchDist = 0;
    if (e.pointerId !== primaryId) return;
    primaryId = null;
    try {
      canvas.releasePointerCapture(e.pointerId);
    } catch {
      /* already released */
    }

    // Suppress everything if a library drop just happened.
    if (S.justDroppedFromLib) {
      S.justDroppedFromLib = false;
      S.isResizing = S.isDragging = S.isPanning = S.isConnDragging = false;
      S.connDragState = null;
      S.lassoRect = null;
      S.lassoStart = null;
      S.mouseDown = false;
      dragSnap = null;
      return;
    }
    if (S.isResizing) {
      // Push the PRE-resize snapshot captured at drag start (undo contract).
      if (dragSnap) pushCapturedUndo(dragSnap);
      S.isResizing = false;
      S.resizeState = null;
      markDirty();
      emit('selection');
    }
    if (S.isDragging && S.mouseMoved) {
      if (dragSnap) pushCapturedUndo(dragSnap);
      // Panels re-read x/y after a move.
      emit('selection');
    }
    dragSnap = null;
    S.isDragging = false;
    S.isPanning = false;
    if (S.isConnDragging) {
      S.isConnDragging = false;
      S.connDragState = null;
    }
    if (S.smartGuides.length) {
      S.smartGuides = [];
      markDirty();
    }
    if (S.lassoRect) {
      const L = S.lassoRect;
      const lassoed = S.shapes.filter(
        (s) => s.x < L.x + L.w && s.x + s.w > L.x && s.y < L.y + L.h && s.y + s.h > L.y,
      );
      S.selected = e.shiftKey
        ? [...new Set([...S.selected, ...lassoed.map((s) => s.id)])]
        : lassoed.map((s) => s.id);
      S.lassoRect = null;
      S.lassoStart = null;
      markDirty();
      emit('selection');
    }
    S.mouseDown = false;
  }

  function onPointerCancel(e: PointerEvent): void {
    // iOS fires this on gesture interruption — reset all transient state.
    pointers.delete(e.pointerId);
    if (pointers.size < 2) S.pinchDist = 0;
    dragSnap = null;
    S.isDragging = S.isResizing = S.isConnDragging = S.isPanning = false;
    S.connDragState = null;
    S.lassoStart = null;
    S.lassoRect = null;
    S.mouseDown = false;
    primaryId = null;
  }

  function onWheel(e: WheelEvent): void {
    e.preventDefault();
    const r = getR();
    const mx = e.clientX - r.left;
    const my = e.clientY - r.top;
    const f = e.deltaY < 0 ? 1.1 : 0.9;
    const nz = clamp(S.zoom * f, 0.2, 4);
    S.panX = mx - (mx - S.panX) * (nz / S.zoom);
    S.panY = my - (my - S.panY) * (nz / S.zoom);
    S.zoom = nz;
    cb.updateZoomLabel();
    markDirty();
  }

  function onDblClick(e: MouseEvent): void {
    const r = getR();
    const wc = toW(e.clientX - r.left, e.clientY - r.top);
    const hit = hitShape(wc.x, wc.y);
    if (hit) cb.openLabelEdit(hit, e.clientX - r.left, e.clientY - r.top);
  }

  function onContextMenu(e: MouseEvent): void {
    e.preventDefault();
    const r = getR();
    const wc = toW(e.clientX - r.left, e.clientY - r.top);
    const hit = hitShape(wc.x, wc.y);
    if (hit && !S.selected.includes(hit.id)) {
      S.selected = [hit.id];
      markDirty();
      emit('selection');
    }
    cb.openContextMenu({
      clientX: e.clientX,
      clientY: e.clientY,
      hasSelection: S.selected.length > 0,
      hasClipboard: S.clipboard.length > 0,
    });
  }

  function onAuxClick(e: MouseEvent): void {
    e.preventDefault();
  }

  canvas.addEventListener('pointerdown', onPointerDown);
  canvas.addEventListener('pointermove', onPointerMove);
  canvas.addEventListener('pointerup', onPointerUp);
  canvas.addEventListener('pointercancel', onPointerCancel);
  canvas.addEventListener('wheel', onWheel, { passive: false });
  canvas.addEventListener('dblclick', onDblClick);
  canvas.addEventListener('contextmenu', onContextMenu);
  canvas.addEventListener('auxclick', onAuxClick);

  return () => {
    canvas.removeEventListener('pointerdown', onPointerDown);
    canvas.removeEventListener('pointermove', onPointerMove);
    canvas.removeEventListener('pointerup', onPointerUp);
    canvas.removeEventListener('pointercancel', onPointerCancel);
    canvas.removeEventListener('wheel', onWheel);
    canvas.removeEventListener('dblclick', onDblClick);
    canvas.removeEventListener('contextmenu', onContextMenu);
    canvas.removeEventListener('auxclick', onAuxClick);
  };
}
